// CE-4 / ITEM 16 — **EVERY GENERATION FAILURE WRITES A STATUS ROW, AND A DASHBOARD MAY NEVER READ
// GREEN OVER FAILING MEMBERS** (her order).
//
// WHAT THIS FILE IS FOR, AND WHY IT IS A `.js` LAW RATHER THAN A CONSTANT INSIDE THE SERVER.
//
// Until this round the ONLY account of a generation that failed was `console.error` — Railway's
// runtime console, unreadable after the fact. That is the exact absence D-1/D-4 (the Living Room
// fault receipt) and MONEY-1 (the stranded portal branch) each had to repair before a cause could be
// named, and it is why nobody could ever answer "how many members got nothing last night".
//
// **THE CARRIER IS `reports`, NEVER A NEW TABLE (R28/R29).** A failure row is `status = 'failed'`
// with `data = NULL`, and **every member-facing read in `server/db/reports.js` already filters
// `data IS NOT NULL`** — so a failure row is invisible to the member BY CONSTRUCTION rather than by
// a filter somebody has to remember. What it is NOT invisible to is her own counts, and that is the
// other half of this round: six queries counted ROWS on that table rather than READINGS.
//
// **THE REASON IS NORMALISED INTO THIS ROSTER AND THE RAW STRING IS NEVER STORED.** Two reasons, and
// both are load-bearing:
//   1. **A raw string can carry a member's words.** `result.error` is usually one of the code's own
//      literals, but at least four sites read `cast.error || "chart_failed"` — a VENDOR'S string,
//      unbounded, on a row this house keeps forever. P2-4C's leak law binds on any durable record.
//   2. **An unbounded reason cannot be said in plain language**, and ITEM A's `plainLanguageViolations`
//      refuses a machine key anywhere outside Advanced. Her morning health line (item 15) reads these
//      rows, so every reason has to arrive already translatable.
// An unrecognised reason is `other` and `other` is HOUSE fault (below) — under-reporting a broken
// thing is the expensive direction (R18/R19).
//
// Imports NOTHING, so the server, the dashboard and a FAST fixture can all load and RUN it.

/** The value written into `reports.status`. One declaration, because six readers compare against it. */
export const GENERATION_FAILED_STATUS = "failed";
/** What a DELIVERED generation's row carries. `saveReport` writes it; the counts filter on data. */
export const GENERATION_SAVED_STATUS = "saved";

// ---------------------------------------------------------------------------
// **THREE FAULTS, NOT TWO, AND THE SPLIT IS WHAT KEEPS HER IT SHEET A WORK ORDER.**
//
// Her ticket sheet's own CC header is *"These are the IT issues for {day} … All of these need to be
// fixed."* A member who never entered a birth time is not a broken thing, and filing her on that
// sheet is a work order about something nobody can fix. So the fault rides the REASON, declared
// once, and item 15's auto-filing asks this rather than a second roster of its own (R28/R29/R31).
//
//   `house`   — this house broke or could not reach a vendor. It belongs on her sheet.
//   `member`  — the member has not given us something we cannot invent (a birth time, a partner,
//               a readable photo). Nothing to fix; it is a product ask, and her word.
//   `content` — the build ran and had nothing true to say, or a gate refused what it made. That is
//               the gate working, and it is a QUALITY signal rather than an outage.
// ---------------------------------------------------------------------------
export const GENERATION_FAULTS = ["house", "member", "content"];

/**
 * THE ROSTER. Every `error: "<literal>"` this server can return, measured off the code rather than
 * imagined (`scripts/generation-failures.mjs` holds that as a biconditional in the direction that
 * matters: a literal the server can return which nobody rostered is RED, and one line closes it).
 *
 * `say` is what she reads. It is a whole sentence in plain English with no machine key, no ruling
 * code and no underscore in it, because it reaches her Overview and `plainLanguageViolations` is
 * asked of it by the fixture.
 */
export const GENERATION_FAILURE_REASONS = {
  // ── The house broke. Her sheet. ─────────────────────────────────────────────────────────────
  generation_failed: { fault: "house", say: "The reading could not be written" },
  chart_failed: { fault: "house", say: "The chart could not be cast" },
  vendor_failed: { fault: "house", say: "An outside service did not answer" },
  vendor: { fault: "house", say: "An outside service did not answer" },
  notConfigured: { fault: "house", say: "A key this house needs is missing" },
  no_key: { fault: "house", say: "A key this house needs is missing" },
  // THE TWO THIS ROUND MINTS. A build that THREW and a reading that was built and could not be
  // STORED are different facts (R18), and the second one is the expensive one: the vendor work was
  // paid for and the member got nothing.
  build_threw: { fault: "house", say: "The reading stopped part way through" },
  save_failed: { fault: "house", say: "The reading was made and could not be saved" },
  // >>> PLACE-1: THE BIGGEST NON-VENDOR CLASS IN THIS HOUSE, AND IT WAS FILED AS HERS. <<<
  // Measured on the live table: of 194 `birth-chart` `requiresInput` rows across 61 members,
  // **ZERO** members failed to give a birth date and **55 typed a birthplace the house never
  // resolved**. `requiresInput` is `member` fault by definition (*"the member has not given us
  // something we cannot invent"*) and is deliberately off her IT sheet — so A3's own measured class
  // (*"46 of 49 birthplaces needing a human's pick"*) has never appeared as work anywhere she looks.
  // It is HOUSE fault: the member gave us her town and we could not place it.
  place_unresolved: { fault: "house", say: "A birthplace could not be placed on the map" },
  // **`other` FAILS CLOSED ONTO THE HOUSE.** A reason nobody rostered is a reason nobody has looked
  // at, and filing it as the member's problem is how a real outage reads as a quiet night.
  other: { fault: "house", say: "Something went wrong that this house has no name for yet" },

  // ── The member has to give us something. Not a work order. ──────────────────────────────────
  requiresInput: { fault: "member", say: "Waiting on something only the member can give" },
  needs_partner: { fault: "member", say: "Waiting on the second person" },
  bad_sign: { fault: "member", say: "Waiting on something only the member can give" },
  retake: { fault: "member", say: "The photo could not be read" },
  confirm: { fault: "member", say: "Waiting for the member to confirm the cards" },

  // ── Nothing true to say, or a gate refused it. ──────────────────────────────────────────────
  no_grounding: { fault: "content", say: "Nothing in the house to build it from" },
  no_match: { fault: "content", say: "Nothing in the house to build it from" },
  no_seal: { fault: "content", say: "No seal fits what was asked" },
  empty: { fault: "content", say: "Nothing in the house to build it from" },
  source_rite: { fault: "content", say: "A gate stopped it before it reached the member" },
  grouping: { fault: "content", say: "A gate stopped it before it reached the member" },
  // >>> THE ONE THE CENSUS COULD NOT SEE, AND IT COST 32 ROWS ACROSS 7 MEMBERS. <<< The clause
  // above it parsed the server's `error:` literals with `[A-Za-z_]+` — no HYPHEN — so
  // `error: "cauldron-method"` was INVISIBLE to the biconditional whose own comment says *"an
  // unrostered one is silently filed as `other` and reads exactly like a rare crash"*. It did.
  // It is the METHOD gate refusing a compiled working, so it is CONTENT fault and takes
  // `grouping`'s own sentence — the gate beside it, doing the same job on the same rail. Filed as
  // `other` it was HOUSE fault, so **a content gate working correctly read as an outage.**
  "cauldron-method": { fault: "content", say: "A gate stopped it before it reached the member" },
};

export const GENERATION_FAILURE_KEYS = Object.keys(GENERATION_FAILURE_REASONS);
/** The one key an unrecognised reason lands on. Named, so nothing hand-types the string twice. */
export const GENERATION_FAILURE_OTHER = "other";

/**
 * Normalise whatever a build handed back into a rostered key.
 *
 * `=== "string"` and not a truthy read: this arrives off a vendor payload as well as off our own
 * literals, and `Boolean({})` is `true` (R136/R137). A number, an object, an Error and an empty
 * string all land on `other`, which is the house-fault key — never dropped, because a failure with
 * an unreadable reason is still a member who got nothing.
 */
export function failureReason(raw) {
  if (typeof raw !== "string") return GENERATION_FAILURE_OTHER;
  const key = raw.trim();
  if (!key) return GENERATION_FAILURE_OTHER;
  return Object.prototype.hasOwnProperty.call(GENERATION_FAILURE_REASONS, key)
    ? key
    : GENERATION_FAILURE_OTHER;
}

/** Which pile a reason belongs to. An unrostered key is `house`, for the reason declared above. */
export function failureFault(key) {
  const entry = GENERATION_FAILURE_REASONS[failureReason(key)];
  return entry ? entry.fault : "house";
}

/** What she reads. Never the key — the key is machine language and this sentence is not. */
export function failureSentence(key) {
  const entry = GENERATION_FAILURE_REASONS[failureReason(key)];
  return entry ? entry.say : GENERATION_FAILURE_REASONS[GENERATION_FAILURE_OTHER].say;
}

/** True when a reason means THIS HOUSE is broken — the only pile that belongs on her IT sheet. */
export function isHouseFault(key) {
  return failureFault(key) === "house";
}

/**
 * **THE ONE SENTENCE A DASHBOARD SAYS ABOUT LAST NIGHT'S FAILURES, AND IT HAS THREE STATES, NOT TWO
 * (R18/R65).**
 *
 * `null` / `undefined` is a reading that HAS NOT ARRIVED and holds still; a summary that arrived is
 * an ANSWER, and zero failures is a real answer with its own sentence. Collapsing the first two puts
 * *"Nothing failed"* on the screen of an owner whose dashboard could not read the database — which
 * is the one wrong thing to tell her and is exactly what her *"never read green over failing
 * members"* clause forbids, one layer up from the rows themselves.
 */
export const FAILURES_UNREAD = "Failures could not be read just now.";
export const FAILURES_CLEAR = "No generation failed in this window.";

export function failureLine(summary) {
  if (!summary || typeof summary !== "object") return null;
  const failures = Math.max(0, Math.trunc(Number(summary.failures) || 0));
  if (!failures) return FAILURES_CLEAR;
  const members = Math.max(0, Math.trunc(Number(summary.membersAffected) || 0));
  const n = failures.toLocaleString("en-US");
  // The member count is the half her sentence is actually about — "failing MEMBERS", not failing
  // rows — so it is never dropped, and a failure whose member could not be counted still says how
  // many readings went nowhere.
  if (!members) return `${n} ${failures === 1 ? "reading" : "readings"} could not be delivered.`;
  return `${n} ${failures === 1 ? "reading" : "readings"} could not be delivered to ${members.toLocaleString("en-US")} ${members === 1 ? "member" : "members"}.`;
}

/**
 * The rows a failures table draws, newest-heaviest first, each already in her words.
 * Returns `null` for a reading that has not arrived, for the same reason `failureLine` does.
 */
export function failureRows(summary) {
  if (!summary || typeof summary !== "object" || !Array.isArray(summary.byReason)) return null;
  return summary.byReason
    .map((r) => ({
      key: failureReason(r && r.reason),
      label: failureSentence(r && r.reason),
      fault: failureFault(r && r.reason),
      count: Math.max(0, Math.trunc(Number(r && r.count) || 0)),
    }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}
